import React, { useState, useEffect, useCallback } from 'react';
import './index.css'; 
import {
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Brain,
  TrendingUp,
  Layers,
  Lock,
  ArrowDown,
  BarChart2,
  Terminal,
  ArrowRight
} from 'lucide-react';

/* --- Reusable UI Components Defined OUTSIDE App --- */

// A frosted glass effect card
const GlassCard = ({ children, className = "", hoverEffect = true, onClick }) => (
  <div 
    onClick={onClick}
    className={`relative overflow-hidden bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-xl p-6 ${hoverEffect ? 'hover:border-cyan-500/30 transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(6,182,212,0.15)]' : ''} ${className}`}
  >
    {children}
  </div>
);

// Primary Call-to-Action button with a glowing effect
const GlowingButton = ({ children, onClick, className = "", primary = true }) => (
  <button
    onClick={onClick}
    className={`relative group px-8 py-4 rounded-lg font-bold tracking-wide transition-all duration-300 transform active:scale-95
      ${primary
        ? 'bg-cyan-600/90 hover:bg-cyan-500 text-white shadow-[0_0_20px_-5px_rgba(8,145,178,0.6)] hover:shadow-[0_0_30px_-5px_rgba(6,182,212,0.8)]'
        : 'bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-white/10 hover:border-white/30'}
      ${className}`}
  >
    {children}
    {primary && <div className="absolute inset-0 rounded-lg ring-2 ring-white/20 group-hover:ring-white/40 transition-all duration-500" />}
  </button>
);

// Standardized section heading
const SectionTitle = ({ pre, title, subtitle }) => (
  <div className="text-center mb-16 relative z-10 px-4">
    {pre && <p className="text-cyan-400 text-sm font-mono tracking-[0.2em] uppercase mb-4">{pre}</p>}
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
      {title}
    </h2>
    {subtitle && <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
  </div>
);

/* --- Background Animation Components --- */
const BackgroundOrbs = () => (
  <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
    <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-20px, 20px); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes pulse-light {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .animate-float { animation: float 10s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 8s ease-in-out infinite; }
        .animate-pulse-light { animation: pulse-light 1.5s ease-in-out infinite; }
    `}</style>
    <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] bg-blue-900/20 rounded-full blur-[120px] animate-pulse-slow" />
    <div className="absolute top-[20%] right-[-5%] w-[35vw] h-[35vw] bg-cyan-900/10 rounded-full blur-[100px] animate-float" />
    <div className="absolute bottom-[-10%] left-[20%] w-[50vw] h-[50vw] bg-slate-900/30 rounded-full blur-[140px]" />
  </div>
);

/* --- Main Application --- */
const App = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [agreedToTos, setAgreedToTos] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null); 
  const [showScrollReminder, setShowScrollReminder] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Smooth scroll handler
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Validation check
  const isReadyToProceed = agreedToTos && selectedTier !== null;

  // --- NOWPayments Invoice Integration ---
  const handlePayment = async () => {
    if (!isReadyToProceed || isProcessing) return;
    
    setIsProcessing(true);
    try {
      // Calls your Cloudflare Function (create-payments.js)
      const response = await fetch('/create-payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: selectedTier }),
      });

      const data = await response.json();

      // Check for invoice_url specifically (Standard Invoice API response)
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        console.error("NOWPayments API Error:", data);
        alert("Payment gateway error. Please contact us or try again.");
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Network Error:", error);
      alert("Network error. Please check your connection and try again.");
      setIsProcessing(false);
    }
  };

  // Scroll listener for nav
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (window.scrollY > window.innerHeight / 2) {
        setShowScrollReminder(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };
  
  const handleSelectTier = (tier) => {
      setSelectedTier(tier);
      setTimeout(() => {
          scrollToSection('checkout-area');
      }, 100);
  };
 
  const getButtonText = () => {
    if (isProcessing) return "Opening Checkout...";
    if (selectedTier === 'standard') {
        return isReadyToProceed ? "Secure Standard Membership" : "Select Tier & Agree to Proceed";
    }
    if (selectedTier === 'strategic') {
        return isReadyToProceed ? "Secure Strategic Membership" : "Select Tier & Agree to Proceed";
    }
    return isReadyToProceed ? "Secure My Membership" : "Select Tier & Agree to Proceed";
  };

  return (
    <div className="min-h-screen bg-[#0B0C15] text-slate-200 font-sans selection:bg-cyan-500/30 selection:text-cyan-100">
      <style>{`
        .flip-b { display: inline-block; transform: scaleX(-1); color: inherit; }
      `}</style>

      <BackgroundOrbs />

      {/* --- Navigation --- */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 border-b ${isScrolled ? 'bg-[#0B0C15]/80 backdrop-blur-lg border-white/5 py-4' : 'bg-transparent border-transparent py-6'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="text-2xl font-bold tracking-tighter text-white flex items-center gap-1">
             <span className="text-cyan-400 flip-b">B</span>
             <span>RESEARCH</span>
          </div>
        </div>
      </nav>

      {/* Scroll Reminder Popup */}
      {showScrollReminder && (
        <div
            className="fixed bottom-8 right-8 z-[100] p-4 bg-cyan-600/95 rounded-xl shadow-2xl cursor-pointer flex items-center gap-3 animate-pulse-light"
            onClick={() => { scrollToSection('pricing'); setShowScrollReminder(false); }}
        >
          <ArrowDown className="w-5 h-5 text-white animate-bounce" />
          <span className="text-white font-semibold text-sm">We love your enthusiasm! Please scroll down and see what we have to offer.</span>
          <button onClick={(e) => { e.stopPropagation(); setShowScrollReminder(false); }} className="ml-2 text-white/80 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* --- Hero Section --- */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-32 px-6 container mx-auto text-center z-10">
        <div className="max-w-4xl mx-auto">
          <div className="inline-block mb-6 px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-900/10 text-cyan-400 text-xs md:text-sm font-mono tracking-widest uppercase">
            For The Serious, Time-Conscious Investor
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-[1.1] tracking-tight">
            Stop Wasting Thousands on <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Fragmented Research.</span>
          </h1>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            We aggregate, verify, and synthesize mission-critical data from over a dozen proprietary and exclusive sources to give you a strategic edge.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <GlowingButton onClick={() => setShowScrollReminder(true)}>
              Apply for Access
            </GlowingButton>
          </div>
        </div>
      </section>

      {/* --- Agitation --- */}
      <section className="py-24 relative z-10 bg-gradient-to-b from-transparent to-[#0B0C15]">
        <div className="container mx-auto px-6">
          <SectionTitle
            pre="The Problem"
            title="The 'Information Gap' is Killing Your Edge"
            subtitle="In the age of information abundance, the scarcity is attention and verification."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Layers className="w-8 h-8 text-amber-400" />, title: "Fragmented Costs", desc: "You pay recurring high fees across multiple platforms, yet you constantly feel you are missing the bigger picture." },
              { icon: <TrendingUp className="w-8 h-8 text-red-400" />, title: "Late to the Alpha", desc: "You spend valuable hours trying to synthesize macro with flow data, only to realize the move has already played out." },
              { icon: <Brain className="w-8 h-8 text-purple-400" />, title: "Analysis Paralysis", desc: "You are left trying to piece together a complex puzzle with missing pieces, leading to hesitation when you should be executing." },
              { icon: <X className="w-8 h-8 text-cyan-400" />, title: "The Doom Scroll", desc: "Wasting hours on X (Twitter), unable to filter real signal from engagement bait, leaving you fatigued and directionless." }
            ].map((item, idx) => (
              <GlassCard key={idx} className="h-full">
                <div className="mb-4 bg-slate-800/50 w-16 h-16 rounded-full flex items-center justify-center border border-white/5">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* --- Solution & Methodology --- */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          <SectionTitle
            pre="The Solution"
            title="Your Research Convergence Engine"
            subtitle="We do not provide 'calls' or 'signals'. We are a Research Intelligence Hub focused entirely on building and supporting high-conviction theses and information asymmetry, delivered as timely as possible."
          />
          <div className="grid md:grid-cols-4 gap-4 relative mt-20">
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent -z-10" />
            {[
              { step: "01", title: "Aggregation", desc: "Ingesting data from proprietary software, gated communities, and smart money networks." },
              { step: "02", title: "Validation", desc: "Cross-referencing narratives against hard data to separate hype from reality." },
              { step: "03", title: "Synthesis", desc: "Connecting the dots to explain why it matters and how it creates an edge." },
              { step: "04", title: "Education", desc: "We teach you to fish. Elevate your thinking to institutional levels." }
            ].map((item, idx) => (
              <div key={idx} className="relative group text-center">
                <div className="w-24 h-24 mx-auto bg-[#0B0C15] border-2 border-slate-700 group-hover:border-cyan-400 rounded-full flex items-center justify-center text-2xl font-bold text-slate-500 group-hover:text-cyan-400 transition-all duration-300 z-10 mb-6 shadow-xl">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm px-4">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- The Approach --- */}
      <section className="py-20 bg-slate-900/20 border-y border-white/5 z-10 relative">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center gap-12">
          <div className="md:w-1/2">
            <h3 className="text-3xl font-bold text-white mb-6">
              We Trade Based on Conviction, <br />
              <span className="text-amber-400">Not Content Quota.</span>
            </h3>
            <div className="space-y-6">
              {[
                { title: "No Forced Updates", desc: "We prioritize conviction over content volume. If there is no edge, we stay silent." },
                { title: "Data-to-Information Funnel", desc: "Distilling raw, noisy data into actionable information you can trust." },
                { title: "Curated Attention", desc: "Laser focus on exactly what you must pay attention to, saving you dozens of hours." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="mt-1 min-w-[24px]"><Check className="w-6 h-6 text-cyan-400" /></div>
                  <div>
                    <h4 className="text-white font-bold">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:w-1/2">
             <GlassCard className="p-8 border-l-4 border-l-cyan-500 bg-gradient-to-br from-slate-900/80 to-slate-800/80">
                <h4 className="text-lg text-cyan-400 font-mono mb-4 uppercase tracking-widest">Our Philosophy</h4>
                <p className="text-xl text-white italic leading-relaxed">
                  "Most investors drown in data while starving for wisdom. We act as the filter. We don't just give you the fish; we show you exactly how the currents are moving so you understand the hunt."
                </p>
             </GlassCard>
          </div>
        </div>
      </section>

      {/* --- Value Stack --- */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          <SectionTitle
            pre="The Investment Logic"
            title="Convert $32,000+ in Costs into Pure Edge"
            subtitle="You no longer have to manage dozens of subscriptions or spend 50+ hours per week searching for an edge."
          />
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2 flex flex-col items-center">
                <h3 className="text-xl font-bold text-white mb-6">Annual Cost Comparison</h3>
                <div className="w-full h-80 flex items-end justify-center gap-6">
                    <div className="relative w-1/3 h-full bg-red-800/50 rounded-lg overflow-hidden flex flex-col justify-end p-3 shadow-2xl transition-all duration-700 hover:scale-[1.03]">
                        <div className="absolute inset-0 bg-red-600/20 blur-xl" />
                        <p className="text-sm font-mono text-red-300">Fragmented Costs</p>
                        <p className="text-2xl font-bold text-red-100">$32,000+</p>
                    </div>
                    <div className="relative w-1/3 h-[30%] bg-cyan-600/70 rounded-lg overflow-hidden flex flex-col justify-end p-3 shadow-2xl transition-all duration-700 hover:scale-[1.03]">
                        <div className="absolute inset-0 bg-cyan-500/20 blur-xl" />
                        <p className="text-sm font-mono text-cyan-300">BRL (Strategic)</p>
                        <p className="text-2xl font-bold text-cyan-100">$1,200</p>
                    </div>
                </div>
            </div>
            <div className="w-full md:w-1/2 space-y-4">
                <h4 className="text-2xl font-bold text-white">The true cost of searching for Alpha.</h4>
                <p className="text-slate-300 mb-6">Your current fragmented stack includes fees for tools such as:</p>
                <ul className="list-disc list-inside text-slate-300 ml-4 space-y-2">
                    <li>Smart Money Flow</li>
                    <li>Tier-1 Research Consortia</li>
                    <li>Deepcharts Data Terminal</li>
                    <li>News Terminal</li>
                    <li>MarketCiper</li>
                    <li>Premium Gated Forums</li>
                    <li>Selected KOL Alpha Groups</li>
                </ul>
                <div className="pt-4">
                    <GlowingButton onClick={() => scrollToSection('pricing')}>
                        Apply for Access
                    </GlowingButton>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Proof of Edge --- */}
      <section className="py-24 bg-[#090a11] relative z-10 border-y border-white/5">
        <div className="container mx-auto px-6">
          <SectionTitle
             pre="Proof of Edge"
             title="Experience Measured in Alpha"
             subtitle="We eat our own cooking. These results reflect the conviction of our synthesis."
          />
          <div className="grid md:grid-cols-3 gap-6 mb-12">
             {[
               { label: "Macro Swing", pnl: "+142%", asset: "Commodities Thesis" },
               { label: "On-Chain Play", pnl: "+450%", asset: "DeFi Governance" },
               { label: "L1 Rotation", pnl: "+85%", asset: "Alt-L1 Basket" },
             ].map((item, i) => (
                <div key={i} className="aspect-[4/3] bg-slate-900 border border-white/10 rounded-lg flex flex-col items-center justify-center relative overflow-hidden group">
                  <div className="absolute inset-0 bg-slate-800" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent flex flex-col justify-end p-6">
                    <p className="text-cyan-400 text-xs uppercase tracking-widest mb-1">{item.label}</p>
                    <h4 className="text-4xl font-bold text-green-400">{item.pnl}</h4>
                    <p className="text-slate-400 text-sm mt-1">{item.asset}</p>
                  </div>
                </div>
             ))}
          </div>
        </div>
      </section>

      {/* --- Authority --- */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-6">
          <GlassCard className="max-w-4xl mx-auto p-10 md:p-14">
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="w-20 h-20 bg-slate-800/50 rounded-full flex-shrink-0 flex items-center justify-center border border-white/5">
                 <span className="text-3xl font-bold text-cyan-400 flip-b">B</span>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-white mb-2">Led by Deep Expertise, Not Hype.</h3>
                <div className="h-1 w-20 bg-cyan-500 mb-6" />
                <p className="text-slate-300 leading-relaxed mb-6">
                  Approaching Two Decades in <strong>Equities & Commodities</strong> + 6 Years in the <strong>Crypto Trenches</strong>.
                </p>
                <p className="text-slate-400 text-sm italic">
                  "I built this resource to solve a problem I faced: cutting through the chaos to find quality research."
                </p>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>
      
      {/* --- FAQ --- */}
      <section className="py-20 bg-black/20 z-10 relative">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: "What makes this different?", a: "Most groups sell you data silos or raw noise. We sell synthesis." },
              { q: "How do I know if this is right for me?", a: "If you have a portfolio to manage and prioritize verified synthesis, this is for you." },
              { q: "Is this worth the investment?", a: "One high-conviction decision derived from our research often covers the fee." },
              { q: "Payment Plan?", a: "No. We operate on 3-month or 1-year commitments." },
              { q: "Refund Policy?", a: "Strict No Refunds." }
            ].map((item, i) => (
              <div key={i} className="border border-white/10 rounded-lg bg-slate-900/40 overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex justify-between items-center p-5 text-left text-slate-200 hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium">{item.q}</span>
                  {expandedFaq === i ? <ChevronUp className="w-5 h-5 text-cyan-400" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                </button>
                {expandedFaq === i && (
                  <div className="p-5 pt-0 text-slate-400 text-sm leading-relaxed border-t border-white/5 mt-2">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* --- Pricing --- */}
      <section id="pricing" className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          <SectionTitle
            pre="Membership"
            title="Secure Your Advantage"
            subtitle="Access is limited. We cap membership to ensure the quality of the community."
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <GlassCard
              className={`flex flex-col relative cursor-pointer ${selectedTier === 'standard' ? 'border-amber-400/50 ring-2 ring-amber-400/30' : ''}`}
              onClick={() => handleSelectTier('standard')}
            >
               <div className="p-2 mb-4">
                 <h3 className="text-xl font-bold text-slate-300">Standard</h3>
                 <p className="text-slate-500 text-sm">Quarterly Commitment</p>
               </div>
               <div className="mb-6">
                 <span className="text-4xl font-bold text-white">$360</span>
                 <span className="text-slate-500"> / 3 months</span>
               </div>
               <ul className="space-y-4 mb-8 flex-grow">
                 <li className="flex gap-3 text-slate-300 text-sm"><Check className="w-5 h-5 text-slate-500" /> Full Research Access</li>
                 <li className="flex gap-3 text-slate-300 text-sm"><Check className="w-5 h-5 text-slate-500" /> Community Chat</li>
               </ul>
               <button className="w-full py-4 rounded-lg border border-white/20 text-white font-bold hover:bg-white/5">Select Standard</button>
            </GlassCard>

            <GlassCard
              className={`flex flex-col relative bg-cyan-900/10 cursor-pointer ${selectedTier === 'strategic' ? 'border-cyan-500/80 ring-2 ring-cyan-500/50' : ''}`}
              onClick={() => handleSelectTier('strategic')}
            >
               <div className="absolute top-0 right-0 bg-cyan-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">Best Value</div>
               <div className="p-2 mb-4">
                 <h3 className="text-xl font-bold text-cyan-400">Strategic Tier</h3>
                 <p className="text-slate-400 text-sm">Annual Commitment</p>
               </div>
               <div className="mb-6">
                 <span className="text-4xl font-bold text-white">$1,200</span>
                 <span className="text-slate-500"> / year</span>
               </div>
               <ul className="space-y-4 mb-8 flex-grow">
                 <li className="flex gap-3 text-white text-sm"><Check className="w-5 h-5 text-cyan-400" /> Full Research Access</li>
                 <li className="flex gap-3 text-amber-400 text-sm font-bold"><Lock className="w-5 h-5" /> Locked-in Price Forever</li>
               </ul>
               <button className="w-full py-4 rounded-lg border border-white/20 text-white font-bold bg-cyan-600/20">Select Strategic</button>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* --- Footer & Checkout --- */}
      <footer id="checkout-area" className="py-12 border-t border-white/10 relative z-10 bg-[#05060a]">
        <div className="container mx-auto px-6 max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Access Is Highly Limited.</h2>
          <p className="text-slate-400 mb-8">The information advantage is temporary—you must act on it now.</p>
          
          <div className="bg-slate-900/50 p-6 rounded-xl border border-white/10 mb-8 text-left">
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="relative pt-1">
                <input
                  type="checkbox"
                  checked={agreedToTos}
                  onChange={(e) => setAgreedToTos(e.target.checked)}
                  className="peer sr-only"
                />
                <div className={`w-6 h-6 border-2 rounded transition-all ${agreedToTos ? 'bg-cyan-500 border-cyan-500' : 'border-slate-500 group-hover:border-slate-300'}`}>
                   {agreedToTos && <Check className="w-4 h-4 text-white absolute top-1.5 left-0.5" />}
                </div>
              </div>
              <span className="text-sm text-slate-400 select-none">
                Yes, I confirm I have read and agree to the Disclosure and Disclaimer below.
                {selectedTier === null && <span className="text-red-400 ml-2"> (Select a tier to continue)</span>}
              </span>
            </label>
          </div>

          <div className={`${isReadyToProceed ? 'opacity-100 pointer-events-auto' : 'opacity-50 pointer-events-none'} transition-all duration-300`}>
             <GlowingButton 
               className="w-full text-lg"
               onClick={handlePayment}
             >
               {getButtonText()}
             </GlowingButton>
          </div>

          <div className="mt-12 text-xs text-slate-600 text-justify leading-relaxed">
            <p className="mb-4"><strong>DISCLOSURE & DISCLAIMER:</strong> All content is for research purposes only. Not financial advice.</p>
            <p>&copy; {new Date().getFullYear()} Bunnin Research Labs. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
